import { resolveWorkspaceMediaUrl } from '../lib/urls';
/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Speaker Dialogue & Web Audio Mixer Store (Task 10.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type TrackType = 'dialogue' | 'bgm' | 'sfx' | 'master';
export type ExportAudioFormat = 'wav' | 'mp3';

export interface SpeakerProfile {
  id: string;
  name: string;
  voiceId: string;
  voiceName: string;
  provider: 'openai' | 'elevenlabs' | 'google' | 'azure' | 'custom_clone';
  gender: 'female' | 'male' | 'neutral';
  color: string;
  defaultSpeed: number; // 0.8 - 1.5
  defaultPitch: number; // -12 - +12 semitones
}

export interface DialogueBlock {
  id: string;
  speakerId: string;
  text: string;
  speed: number;
  pitch: number;
  pauseAfter: number; // seconds (0.2 - 2.0)
  audioUrl?: string;
  duration: number; // seconds
  startTime: number; // timeline offset
  endTime: number;
  status: 'idle' | 'synthesizing' | 'ready' | 'error';
}

export interface MixerClip {
  id: string;
  trackId: string;
  name: string;
  audioUrl: string;
  startOffsetSec: number;
  durationSec: number;
  volume: number; // 0 - 1
  fadeInMs: number;
  fadeOutMs: number;
  loop?: boolean;
  duckingEnabled?: boolean;
}

export interface MixerTrack {
  id: string;
  name: string;
  type: TrackType;
  volume: number; // 0 - 1
  pan: number; // -1 to 1
  muted: boolean;
  solo: boolean;
  clips: MixerClip[];
  speakerId?: string;
}

export interface AudioDuckingSettings {
  enabled: boolean;
  duckingPercent: number; // e.g. 20 -> drops volume to 20%
  fadeDurationMs: number; // e.g. 250ms
  recoveryDurationMs: number; // e.g. 400ms
}

export interface DuckingRegion {
  startSec: number;
  endSec: number;
  duckingRatio: number;
  speechTrackName: string;
}

export interface BgmPreset {
  id: string;
  title: string;
  genre: string;
  durationSec: number;
  bpm: number;
  audioUrl: string;
}

export interface AudioMixerState {
  // Project Info
  projectTitle: string;
  activeStudioTab: 'script' | 'timeline' | 'export';

  // Multi-Speaker Script
  speakers: SpeakerProfile[];
  dialogueBlocks: DialogueBlock[];

  // Timeline & Mixer
  tracks: MixerTrack[];
  duckingSettings: AudioDuckingSettings;
  duckingRegions: DuckingRegion[];

  // Playback Controller
  isPlaying: boolean;
  currentTime: number; // timeline playhead in seconds
  totalDuration: number;
  playbackRate: number; // 0.8, 1.0, 1.2, 1.5
  masterVolume: number; // 0 - 1
  zoomLevel: number; // 1, 2, 4
  isLooping: boolean;

  // Preset BGM Library
  bgmPresets: BgmPreset[];
  selectedBgmId: string | null;

  // Export State
  isExporting: boolean;
  exportProgress: number; // 0 - 100
  exportFormat: ExportAudioFormat;
  exportSampleRate: number; // 44100 or 48000
  exportedAudioUrl: string | null;

  // Actions: Script Management
  setProjectTitle: (title: string) => void;
  setActiveStudioTab: (tab: 'script' | 'timeline' | 'export') => void;
  addSpeaker: (speaker: Omit<SpeakerProfile, 'id'>) => SpeakerProfile;
  updateSpeaker: (id: string, updates: Partial<SpeakerProfile>) => void;
  deleteSpeaker: (id: string) => void;

  addDialogueBlock: (block?: Partial<DialogueBlock>) => DialogueBlock;
  updateDialogueBlock: (id: string, updates: Partial<DialogueBlock>) => void;
  deleteDialogueBlock: (id: string) => void;
  moveDialogueBlock: (fromIndex: number, toIndex: number) => void;
  loadScriptTemplate: (templateId: 'podcast' | 'news' | 'drama') => void;
  recalculateTimelineTimestamps: () => void;
  synthesizeDialogueBlock: (id: string) => Promise<void>;
  synthesizeAllBlocks: () => Promise<void>;

  // Actions: Timeline & Mixer
  syncScriptToTimeline: () => void;
  updateTrack: (trackId: string, updates: Partial<MixerTrack>) => void;
  setTrackVolume: (trackId: string, volume: number) => void;
  setTrackPan: (trackId: string, pan: number) => void;
  toggleTrackMute: (trackId: string) => void;
  toggleTrackSolo: (trackId: string) => void;
  setDuckingSettings: (settings: Partial<AudioDuckingSettings>) => void;
  computeDuckingRegions: () => void;

  selectBgmPreset: (bgmId: string | null) => void;
  addCustomBgmTrack: (file: { name: string; url: string; duration: number }) => void;

  // Actions: Playback
  playTimeline: () => void;
  pauseTimeline: () => void;
  stopTimeline: () => void;
  seekTimeline: (timeSec: number) => void;
  setPlaybackRate: (rate: number) => void;
  setMasterVolume: (vol: number) => void;
  setZoomLevel: (zoom: number) => void;
  toggleLoop: () => void;

  // Actions: Render & Export
  startExport: (format?: ExportAudioFormat, sampleRate?: number) => Promise<string>;
  resetExport: () => void;

  // Selectors
  getEstimatedCredits: () => number;
  getTotalWordCount: () => number;
}

export const SEED_SPEAKERS: SpeakerProfile[] = [
  {
    id: 'spk_host_alex',
    name: 'Alex Rivera (Host)',
    voiceId: 'eleven-adam',
    voiceName: 'Adam (ElevenLabs)',
    provider: 'elevenlabs',
    gender: 'male',
    color: '#06B6D4',
    defaultSpeed: 1.0,
    defaultPitch: 0,
  },
  {
    id: 'spk_guest_sophia',
    name: 'Dr. Sophia Minh (Guest)',
    voiceId: 'clone-sophia-exec',
    voiceName: 'Sophia Executive (Cloned)',
    provider: 'custom_clone',
    gender: 'female',
    color: '#10B981',
    defaultSpeed: 1.02,
    defaultPitch: 1,
  },
  {
    id: 'spk_expert_viet',
    name: 'Việt Quang (Tech Expert)',
    voiceId: 'clone-viet-techlead',
    voiceName: 'Việt Quang TechLead (Cloned)',
    provider: 'custom_clone',
    gender: 'male',
    color: '#F59E0B',
    defaultSpeed: 1.05,
    defaultPitch: 0,
  },
];

export const SEED_DIALOGUE_BLOCKS: DialogueBlock[] = [
  {
    id: 'blk_1',
    speakerId: 'spk_host_alex',
    text: 'Chào mừng quý thính giả đến với Sophia Tech Talk. Hôm nay chúng ta sẽ thảo luận về kỷ nguyên AI phân tán trên mạng biên Cloudflare Workers.',
    speed: 1.0,
    pitch: 0,
    pauseAfter: 0.6,
    duration: 6.8,
    startTime: 0,
    endTime: 6.8,
    status: 'ready',
    audioUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
  },
  {
    id: 'blk_2',
    speakerId: 'spk_guest_sophia',
    text: 'Cảm ơn Alex. Kiến trúc Edge-native cho phép chúng tôi tổng hợp âm thanh đa rãnh và phối nhạc thông minh với độ trễ phản hồi chỉ tính bằng mili-giây.',
    speed: 1.02,
    pitch: 1,
    pauseAfter: 0.5,
    duration: 7.2,
    startTime: 7.4,
    endTime: 14.6,
    status: 'ready',
    audioUrl: resolveWorkspaceMediaUrl('audio/samples/clone-sophia-exec.mp3'),
  },
  {
    id: 'blk_3',
    speakerId: 'spk_expert_viet',
    text: 'Đặc biệt là cơ chế Smart Audio Ducking phía Client bằng Web Audio API GainNode, nhạc nền tự động hạ âm lượng 80% khi có tiếng người nói mà không tốn máy chủ FFmpeg.',
    speed: 1.05,
    pitch: 0,
    pauseAfter: 0.8,
    duration: 8.5,
    startTime: 15.1,
    endTime: 23.6,
    status: 'ready',
    audioUrl: resolveWorkspaceMediaUrl('audio/samples/clone-viet-techlead.mp3'),
  },
  {
    id: 'blk_4',
    speakerId: 'spk_host_alex',
    text: 'Thật kinh ngạc! Chúng ta hãy cùng lắng nghe và hòa âm bản thu âm này ngay trên trình duyệt.',
    speed: 1.0,
    pitch: 0,
    pauseAfter: 1.0,
    duration: 4.8,
    startTime: 24.4,
    endTime: 29.2,
    status: 'ready',
    audioUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
  },
];

export const BGM_PRESETS: BgmPreset[] = [
  {
    id: 'bgm_ambient_tech',
    title: 'Future Horizon (Ambient Tech)',
    genre: 'Electronic / Ambient',
    durationSec: 90,
    bpm: 110,
    audioUrl: resolveWorkspaceMediaUrl('audio/bgm/future-horizon.mp3'),
  },
  {
    id: 'bgm_cinematic_pulse',
    title: 'Pulse of Innovation (Cinematic)',
    genre: 'Cinematic / Modern',
    durationSec: 120,
    bpm: 124,
    audioUrl: resolveWorkspaceMediaUrl('audio/bgm/pulse-of-innovation.mp3'),
  },
  {
    id: 'bgm_lofi_study',
    title: 'Late Night Coding (Lo-Fi Chill)',
    genre: 'Lo-Fi / Beats',
    durationSec: 105,
    bpm: 85,
    audioUrl: resolveWorkspaceMediaUrl('audio/bgm/late-night-coding.mp3'),
  },
  {
    id: 'bgm_acoustic_warmth',
    title: 'Inspiring Stories (Acoustic Warmth)',
    genre: 'Acoustic / Corporate',
    durationSec: 80,
    bpm: 96,
    audioUrl: resolveWorkspaceMediaUrl('audio/bgm/inspiring-stories.mp3'),
  },
];

export const useAudioMixerStore = create<AudioMixerState>((set, get) => ({
  projectTitle: 'AI Tech Podcast: Edge Audio Revolution',
  activeStudioTab: 'script',

  speakers: [...SEED_SPEAKERS],
  dialogueBlocks: [...SEED_DIALOGUE_BLOCKS],

  tracks: [
    {
      id: 'trk_dialogue_alex',
      name: 'Alex Rivera (Host)',
      type: 'dialogue',
      volume: 1.0,
      pan: -0.15,
      muted: false,
      solo: false,
      speakerId: 'spk_host_alex',
      clips: [
        {
          id: 'clip_1',
          trackId: 'trk_dialogue_alex',
          name: 'Welcome to Tech Talk',
          audioUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
          startOffsetSec: 0,
          durationSec: 6.8,
          volume: 1.0,
          fadeInMs: 50,
          fadeOutMs: 80,
          duckingEnabled: true,
        },
        {
          id: 'clip_4',
          trackId: 'trk_dialogue_alex',
          name: 'Closing remarks',
          audioUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
          startOffsetSec: 24.4,
          durationSec: 4.8,
          volume: 1.0,
          fadeInMs: 50,
          fadeOutMs: 80,
          duckingEnabled: true,
        },
      ],
    },
    {
      id: 'trk_dialogue_guest',
      name: 'Dr. Sophia & Việt Quang',
      type: 'dialogue',
      volume: 1.0,
      pan: 0.15,
      muted: false,
      solo: false,
      clips: [
        {
          id: 'clip_2',
          trackId: 'trk_dialogue_guest',
          name: 'Edge Architecture Insights',
          audioUrl: resolveWorkspaceMediaUrl('audio/samples/clone-sophia-exec.mp3'),
          startOffsetSec: 7.4,
          durationSec: 7.2,
          volume: 1.0,
          fadeInMs: 50,
          fadeOutMs: 80,
          duckingEnabled: true,
        },
        {
          id: 'clip_3',
          trackId: 'trk_dialogue_guest',
          name: 'Smart Ducking Explanation',
          audioUrl: resolveWorkspaceMediaUrl('audio/samples/clone-viet-techlead.mp3'),
          startOffsetSec: 15.1,
          durationSec: 8.5,
          volume: 1.0,
          fadeInMs: 50,
          fadeOutMs: 80,
          duckingEnabled: true,
        },
      ],
    },
    {
      id: 'trk_bgm_master',
      name: 'Background Music (BGM)',
      type: 'bgm',
      volume: 0.65,
      pan: 0,
      muted: false,
      solo: false,
      clips: [
        {
          id: 'clip_bgm',
          trackId: 'trk_bgm_master',
          name: 'Future Horizon (Ambient Tech)',
          audioUrl: resolveWorkspaceMediaUrl('audio/bgm/future-horizon.mp3'),
          startOffsetSec: 0,
          durationSec: 32.0,
          volume: 0.65,
          fadeInMs: 400,
          fadeOutMs: 800,
          loop: true,
          duckingEnabled: false,
        },
      ],
    },
  ],

  duckingSettings: {
    enabled: true,
    duckingPercent: 20, // Duck down to 20%
    fadeDurationMs: 250,
    recoveryDurationMs: 400,
  },

  duckingRegions: [
    { startSec: 0, endSec: 6.8, duckingRatio: 0.2, speechTrackName: 'Alex Rivera (Host)' },
    { startSec: 7.4, endSec: 14.6, duckingRatio: 0.2, speechTrackName: 'Dr. Sophia Minh' },
    { startSec: 15.1, endSec: 23.6, duckingRatio: 0.2, speechTrackName: 'Việt Quang' },
    { startSec: 24.4, endSec: 29.2, duckingRatio: 0.2, speechTrackName: 'Alex Rivera (Host)' },
  ],

  isPlaying: false,
  currentTime: 0,
  totalDuration: 32.0,
  playbackRate: 1.0,
  masterVolume: 0.95,
  zoomLevel: 1.0,
  isLooping: false,

  bgmPresets: [...BGM_PRESETS],
  selectedBgmId: 'bgm_ambient_tech',

  isExporting: false,
  exportProgress: 0,
  exportFormat: 'wav',
  exportSampleRate: 48000,
  exportedAudioUrl: null,

  setProjectTitle: (title) => set({ projectTitle: title }),
  setActiveStudioTab: (tab) => set({ activeStudioTab: tab }),

  addSpeaker: (speakerData) => {
    const newSpeaker: SpeakerProfile = {
      ...speakerData,
      id: `spk_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    };
    set((state) => ({ speakers: [...state.speakers, newSpeaker] }));
    return newSpeaker;
  },

  updateSpeaker: (id, updates) => {
    set((state) => ({
      speakers: state.speakers.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    }));
  },

  deleteSpeaker: (id) => {
    const { speakers, dialogueBlocks } = get();
    if (speakers.length <= 1) return; // Keep at least one speaker
    const remainingSpeakers = speakers.filter((s) => s.id !== id);
    const fallbackSpeakerId = remainingSpeakers[0].id;

    set({
      speakers: remainingSpeakers,
      dialogueBlocks: dialogueBlocks.map((b) =>
        b.speakerId === id ? { ...b, speakerId: fallbackSpeakerId } : b
      ),
    });
  },

  addDialogueBlock: (partialBlock) => {
    const { speakers, dialogueBlocks } = get();
    const defaultSpeakerId = speakers[0]?.id || 'spk_default';

    // Calculate start time based on last block
    let startTime = 0;
    if (dialogueBlocks.length > 0) {
      const lastBlock = dialogueBlocks[dialogueBlocks.length - 1];
      startTime = +(lastBlock.endTime + (lastBlock.pauseAfter || 0.5)).toFixed(2);
    }

    const estimatedDuration = Math.max(2.0, +((partialBlock?.text?.split(/\s+/).length || 5) * 0.35).toFixed(2));

    const newBlock: DialogueBlock = {
      id: `blk_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      speakerId: partialBlock?.speakerId || defaultSpeakerId,
      text: partialBlock?.text || 'Nhập câu thoại mới cho nhân vật tại đây...',
      speed: partialBlock?.speed ?? 1.0,
      pitch: partialBlock?.pitch ?? 0,
      pauseAfter: partialBlock?.pauseAfter ?? 0.5,
      duration: partialBlock?.duration ?? estimatedDuration,
      startTime,
      endTime: +(startTime + estimatedDuration).toFixed(2),
      status: 'idle',
      ...partialBlock,
    };

    set((state) => {
      const updatedBlocks = [...state.dialogueBlocks, newBlock];
      return { dialogueBlocks: updatedBlocks };
    });

    get().recalculateTimelineTimestamps();
    return newBlock;
  },

  updateDialogueBlock: (id, updates) => {
    set((state) => ({
      dialogueBlocks: state.dialogueBlocks.map((b) => (b.id === id ? { ...b, ...updates } : b)),
    }));
    get().recalculateTimelineTimestamps();
  },

  deleteDialogueBlock: (id) => {
    set((state) => ({
      dialogueBlocks: state.dialogueBlocks.filter((b) => b.id !== id),
    }));
    get().recalculateTimelineTimestamps();
  },

  moveDialogueBlock: (fromIndex, toIndex) => {
    const { dialogueBlocks } = get();
    if (fromIndex < 0 || fromIndex >= dialogueBlocks.length || toIndex < 0 || toIndex >= dialogueBlocks.length) {
      return;
    }
    const updated = [...dialogueBlocks];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    set({ dialogueBlocks: updated });
    get().recalculateTimelineTimestamps();
  },

  recalculateTimelineTimestamps: () => {
    const { dialogueBlocks } = get();
    let cursor = 0;

    const recalculated = dialogueBlocks.map((block) => {
      const duration = block.duration > 0 ? block.duration : Math.max(1.5, block.text.split(/\s+/).length * 0.35);
      const startTime = +cursor.toFixed(2);
      const endTime = +(cursor + duration).toFixed(2);
      cursor = +(endTime + (block.pauseAfter || 0.5)).toFixed(2);
      return {
        ...block,
        duration,
        startTime,
        endTime,
      };
    });

    const totalDuration = Math.max(10, +cursor.toFixed(2));
    set({ dialogueBlocks: recalculated, totalDuration });
    get().computeDuckingRegions();
  },

  synthesizeDialogueBlock: async (id) => {
    set((state) => ({
      dialogueBlocks: state.dialogueBlocks.map((b) =>
        b.id === id ? { ...b, status: 'synthesizing' } : b
      ),
    }));

    await new Promise((res) => setTimeout(res, 500));

    set((state) => ({
      dialogueBlocks: state.dialogueBlocks.map((b) => {
        if (b.id !== id) return b;
        const words = b.text.split(/\s+/).length;
        const duration = +(Math.max(2.0, words * 0.38) / (b.speed || 1.0)).toFixed(2);
        return {
          ...b,
          status: 'ready',
          duration,
          audioUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
        };
      }),
    }));

    get().recalculateTimelineTimestamps();
    get().syncScriptToTimeline();
  },

  synthesizeAllBlocks: async () => {
    set((state) => ({
      dialogueBlocks: state.dialogueBlocks.map((b) => ({ ...b, status: 'synthesizing' })),
    }));

    await new Promise((res) => setTimeout(res, 800));

    set((state) => ({
      dialogueBlocks: state.dialogueBlocks.map((b) => {
        const words = b.text.split(/\s+/).length;
        const duration = +(Math.max(2.0, words * 0.38) / (b.speed || 1.0)).toFixed(2);
        return {
          ...b,
          status: 'ready',
          duration,
          audioUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
        };
      }),
    }));

    get().recalculateTimelineTimestamps();
    get().syncScriptToTimeline();
  },

  loadScriptTemplate: (templateId) => {
    if (templateId === 'news') {
      set({
        projectTitle: 'Bản Tin Thời Sự: Trí Tuệ Nhân Tạo & Chuyển Đổi Số',
        speakers: [
          {
            id: 'spk_anchor_1',
            name: 'MC Hải Đăng (Nam Chính)',
            voiceId: 'azure-namminh',
            voiceName: 'Nam Minh (Azure Neural)',
            provider: 'azure',
            gender: 'male',
            color: '#3B82F6',
            defaultSpeed: 1.05,
            defaultPitch: 0,
          },
          {
            id: 'spk_anchor_2',
            name: 'BTV Hoài My (Nữ Chính)',
            voiceId: 'azure-hoaimy',
            voiceName: 'Hoài My (Azure Neural)',
            provider: 'azure',
            gender: 'female',
            color: '#EC4899',
            defaultSpeed: 1.02,
            defaultPitch: 0,
          },
        ],
        dialogueBlocks: [
          {
            id: 'blk_news_1',
            speakerId: 'spk_anchor_1',
            text: 'Kính chào quý vị và các bạn đang theo dõi bản tin công nghệ chiều nay.',
            speed: 1.05,
            pitch: 0,
            pauseAfter: 0.4,
            duration: 4.2,
            startTime: 0,
            endTime: 4.2,
            status: 'ready',
            audioUrl: resolveWorkspaceMediaUrl('audio/samples/azure-namminh.mp3'),
          },
          {
            id: 'blk_news_2',
            speakerId: 'spk_anchor_2',
            text: 'Thưa quý vị, nền tảng Sophia Platform vừa chính thức công bố công nghệ hòa âm kịch bản phân vai trực tiếp trên trình duyệt Web.',
            speed: 1.02,
            pitch: 0,
            pauseAfter: 0.5,
            duration: 6.8,
            startTime: 4.6,
            endTime: 11.4,
            status: 'ready',
            audioUrl: resolveWorkspaceMediaUrl('audio/samples/azure-hoaimy.mp3'),
          },
          {
            id: 'blk_news_3',
            speakerId: 'spk_anchor_1',
            text: 'Giải pháp này giúp các tòa soạn và nhà sáng tạo nội dung cắt giảm 90% thời gian hậu kỳ âm thanh.',
            speed: 1.05,
            pitch: 0,
            pauseAfter: 0.8,
            duration: 5.6,
            startTime: 11.9,
            endTime: 17.5,
            status: 'ready',
            audioUrl: resolveWorkspaceMediaUrl('audio/samples/azure-namminh.mp3'),
          },
        ],
      });
    } else if (templateId === 'drama') {
      set({
        projectTitle: 'Radio Drama: Bí Mật Hành Tinh Xanh',
        speakers: [
          {
            id: 'spk_captain',
            name: 'Thuyền Trưởng Vance',
            voiceId: 'openai-onyx',
            voiceName: 'Onyx (OpenAI HD)',
            provider: 'openai',
            gender: 'male',
            color: '#6366F1',
            defaultSpeed: 0.95,
            defaultPitch: -2,
          },
          {
            id: 'spk_ai_nav',
            name: 'Hệ Thống Dẫn Đường AI (ARIA)',
            voiceId: 'eleven-rachel',
            voiceName: 'Rachel (ElevenLabs)',
            provider: 'elevenlabs',
            gender: 'female',
            color: '#10B981',
            defaultSpeed: 1.1,
            defaultPitch: 2,
          },
        ],
        dialogueBlocks: [
          {
            id: 'blk_drama_1',
            speakerId: 'spk_captain',
            text: 'ARIA, kiểm tra mức độ bức xạ trong khu vực tầng khí quyển ngoài.',
            speed: 0.95,
            pitch: -2,
            pauseAfter: 0.6,
            duration: 4.5,
            startTime: 0,
            endTime: 4.5,
            status: 'ready',
            audioUrl: resolveWorkspaceMediaUrl('audio/samples/openai-onyx.mp3'),
          },
          {
            id: 'blk_drama_2',
            speakerId: 'spk_ai_nav',
            text: 'Báo cáo thuyền trưởng: Mức năng lượng ổn định. Tín hiệu liên lạc từ trạm không gian đang được khôi phục.',
            speed: 1.1,
            pitch: 2,
            pauseAfter: 0.8,
            duration: 6.2,
            startTime: 5.1,
            endTime: 11.3,
            status: 'ready',
            audioUrl: resolveWorkspaceMediaUrl('audio/samples/eleven-rachel.mp3'),
          },
        ],
      });
    } else {
      // Default podcast template
      set({
        projectTitle: 'AI Tech Podcast: Edge Audio Revolution',
        speakers: [...SEED_SPEAKERS],
        dialogueBlocks: [...SEED_DIALOGUE_BLOCKS],
      });
    }

    get().recalculateTimelineTimestamps();
    get().syncScriptToTimeline();
  },

  syncScriptToTimeline: () => {
    const { dialogueBlocks, speakers, tracks, totalDuration } = get();

    // Map blocks to speaker dialogue tracks
    const updatedTracks: MixerTrack[] = [];

    // Group blocks by speaker
    speakers.forEach((speaker) => {
      const speakerBlocks = dialogueBlocks.filter((b) => b.speakerId === speaker.id);
      const clips: MixerClip[] = speakerBlocks.map((b) => ({
        id: `clip_${b.id}`,
        trackId: `trk_${speaker.id}`,
        name: b.text.slice(0, 30) + '...',
        audioUrl: b.audioUrl || resolveWorkspaceMediaUrl('audio/samples/eleven-adam.mp3'),
        startOffsetSec: b.startTime,
        durationSec: b.duration,
        volume: 1.0,
        fadeInMs: 50,
        fadeOutMs: 80,
        duckingEnabled: true,
      }));

      updatedTracks.push({
        id: `trk_${speaker.id}`,
        name: speaker.name,
        type: 'dialogue',
        volume: 1.0,
        pan: speaker.gender === 'female' ? 0.15 : -0.15,
        muted: false,
        solo: false,
        speakerId: speaker.id,
        clips,
      });
    });

    // Retain or re-add BGM track
    const existingBgmTrack = tracks.find((t) => t.type === 'bgm');
    if (existingBgmTrack) {
      // Extend BGM clip duration to match total timeline
      const bgmClips: MixerClip[] = existingBgmTrack.clips.map((c) => ({
        ...c,
        durationSec: Math.max(c.durationSec, totalDuration),
      }));
      updatedTracks.push({ ...existingBgmTrack, clips: bgmClips });
    } else {
      updatedTracks.push({
        id: 'trk_bgm_master',
        name: 'Background Music (BGM)',
        type: 'bgm',
        volume: 0.65,
        pan: 0,
        muted: false,
        solo: false,
        clips: [
          {
            id: 'clip_bgm',
            trackId: 'trk_bgm_master',
            name: 'Future Horizon (Ambient Tech)',
            audioUrl: resolveWorkspaceMediaUrl('audio/bgm/future-horizon.mp3'),
            startOffsetSec: 0,
            durationSec: Math.max(30, totalDuration),
            volume: 0.65,
            fadeInMs: 400,
            fadeOutMs: 800,
            loop: true,
            duckingEnabled: false,
          },
        ],
      });
    }

    set({ tracks: updatedTracks });
    get().computeDuckingRegions();
  },

  updateTrack: (trackId, updates) => {
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, ...updates } : t)),
    }));
  },

  setTrackVolume: (trackId, volume) => {
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId ? { ...t, volume: Math.max(0, Math.min(1, volume)) } : t
      ),
    }));
  },

  setTrackPan: (trackId, pan) => {
    set((state) => ({
      tracks: state.tracks.map((t) =>
        t.id === trackId ? { ...t, pan: Math.max(-1, Math.min(1, pan)) } : t
      ),
    }));
  },

  toggleTrackMute: (trackId) => {
    set((state) => ({
      tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, muted: !t.muted } : t)),
    }));
  },

  toggleTrackSolo: (trackId) => {
    set((state) => {
      const target = state.tracks.find((t) => t.id === trackId);
      const isSolo = target ? !target.solo : false;
      return {
        tracks: state.tracks.map((t) => (t.id === trackId ? { ...t, solo: isSolo } : t)),
      };
    });
  },

  setDuckingSettings: (newSettings) => {
    set((state) => ({
      duckingSettings: { ...state.duckingSettings, ...newSettings },
    }));
    get().computeDuckingRegions();
  },

  computeDuckingRegions: () => {
    const { tracks, duckingSettings } = get();
    if (!duckingSettings.enabled) {
      set({ duckingRegions: [] });
      return;
    }

    const ratio = Math.max(0, Math.min(1, duckingSettings.duckingPercent / 100));
    const dialogueTracks = tracks.filter((t) => t.type === 'dialogue');

    const regions: DuckingRegion[] = [];
    dialogueTracks.forEach((trk) => {
      trk.clips.forEach((clip) => {
        if (clip.duckingEnabled) {
          regions.push({
            startSec: clip.startOffsetSec,
            endSec: clip.startOffsetSec + clip.durationSec,
            duckingRatio: ratio,
            speechTrackName: trk.name,
          });
        }
      });
    });

    // Sort by startSec
    regions.sort((a, b) => a.startSec - b.startSec);
    set({ duckingRegions: regions });
  },

  selectBgmPreset: (bgmId) => {
    const { bgmPresets, tracks, totalDuration } = get();
    const preset = bgmPresets.find((b) => b.id === bgmId);

    set({ selectedBgmId: bgmId });

    if (!preset) {
      // Remove BGM
      set((state) => ({
        tracks: state.tracks.filter((t) => t.type !== 'bgm'),
      }));
      return;
    }

    const updatedTracks = tracks.map((t) => {
      if (t.type !== 'bgm') return t;
      return {
        ...t,
        clips: [
          {
            id: `clip_${preset.id}`,
            trackId: t.id,
            name: preset.title,
            audioUrl: preset.audioUrl,
            startOffsetSec: 0,
            durationSec: Math.max(preset.durationSec, totalDuration),
            volume: t.volume,
            fadeInMs: 400,
            fadeOutMs: 800,
            loop: true,
            duckingEnabled: false,
          },
        ],
      };
    });

    set({ tracks: updatedTracks });
  },

  addCustomBgmTrack: (file) => {
    const { tracks, totalDuration } = get();
    const updatedTracks = tracks.map((t) => {
      if (t.type !== 'bgm') return t;
      return {
        ...t,
        clips: [
          {
            id: `clip_custom_${Date.now()}`,
            trackId: t.id,
            name: file.name,
            audioUrl: file.url,
            startOffsetSec: 0,
            durationSec: Math.max(file.duration || 60, totalDuration),
            volume: t.volume,
            fadeInMs: 300,
            fadeOutMs: 600,
            loop: true,
            duckingEnabled: false,
          },
        ],
      };
    });
    set({ tracks: updatedTracks, selectedBgmId: 'custom' });
  },

  playTimeline: () => {
    set({ isPlaying: true });
  },

  pauseTimeline: () => {
    set({ isPlaying: false });
  },

  stopTimeline: () => {
    set({ isPlaying: false, currentTime: 0 });
  },

  seekTimeline: (timeSec) => {
    const { totalDuration } = get();
    const clamped = Math.max(0, Math.min(totalDuration, timeSec));
    set({ currentTime: clamped });
  },

  setPlaybackRate: (rate) => set({ playbackRate: rate }),
  setMasterVolume: (vol) => set({ masterVolume: Math.max(0, Math.min(1, vol)) }),
  setZoomLevel: (zoom) => set({ zoomLevel: zoom }),
  toggleLoop: () => set((state) => ({ isLooping: !state.isLooping })),

  startExport: async (format = 'wav', sampleRate = 48000) => {
    set({
      isExporting: true,
      exportProgress: 0,
      exportFormat: format,
      exportSampleRate: sampleRate,
      exportedAudioUrl: null,
    });

    // Simulate multi-track mixdown pipeline
    for (let i = 10; i <= 90; i += 20) {
      await new Promise((res) => setTimeout(res, 120));
      set({ exportProgress: i });
    }

    await new Promise((res) => setTimeout(res, 150));
    const dummyExportUrl = resolveWorkspaceMediaUrl(`audio/rendered/mix_${Date.now()}.${format}`);
    set({
      isExporting: false,
      exportProgress: 100,
      exportedAudioUrl: dummyExportUrl,
    });

    return dummyExportUrl;
  },

  resetExport: () => {
    set({
      isExporting: false,
      exportProgress: 0,
      exportedAudioUrl: null,
    });
  },

  getEstimatedCredits: () => {
    const { dialogueBlocks } = get();
    const totalChars = dialogueBlocks.reduce((acc, b) => acc + b.text.length, 0);
    // Standard TTS rate: 0.15 credit per 100 characters + 1 credit mixing fee
    return +(totalChars * 0.0015 + 1.0).toFixed(1);
  },

  getTotalWordCount: () => {
    const { dialogueBlocks } = get();
    return dialogueBlocks.reduce((acc, b) => acc + (b.text.trim() ? b.text.trim().split(/\s+/).length : 0), 0);
  },
}));
